import { useCallback, useState } from 'react'
import { CameraPosition } from '@/api/controlUnitApi/types.gen'
import { head, includes, isEmpty } from 'lodash-es'

const selectionKeyFor = (source: string, camera: CameraPosition) => `${source}:${camera}`

const useHardwareCamera = (source: string, cameras: CameraPosition[]) => {
  const [chosen, setChosen] = useState<CameraPosition>('FL')
  const camera = !isEmpty(cameras) && !includes(cameras, chosen) ? (head(cameras) ?? chosen) : chosen
  const selectionKey = selectionKeyFor(source, camera)

  const [draftKey, setDraftKey] = useState(selectionKey)
  const [focusValue, setFocusValue] = useState<number | undefined>(undefined)
  const [savedFocusValue, setSavedFocusValue] = useState<number | undefined>(undefined)
  const [saveVisible, setSaveVisible] = useState(false)
  const [initialFocus, setInitialFocus] = useState<ReadonlyMap<string, number>>(() => new Map())

  if (draftKey !== selectionKey) {
    setDraftKey(selectionKey)
    setFocusValue(undefined)
    setSavedFocusValue(undefined)
    setSaveVisible(false)
  }

  const applyServerFocus = useCallback(
    (level: number) => {
      setDraftKey(selectionKey)
      setFocusValue(level)
      setSavedFocusValue(level)
      setSaveVisible(false)
      setInitialFocus(previous => {
        if (previous.has(selectionKey)) return previous
        const next = new Map(previous)
        next.set(selectionKey, level)
        return next
      })
    },
    [selectionKey]
  )

  const setFocusDraft = (level: number) => {
    setFocusValue(level)
    setSaveVisible(savedFocusValue !== undefined && level !== savedFocusValue)
  }

  const commitSavedFocus = (level: number) => {
    setFocusValue(level)
    setSavedFocusValue(level)
    setSaveVisible(false)
  }

  const draftMatchesSelection = draftKey === selectionKey

  return {
    camera,
    setCamera: setChosen,
    focusValue: draftMatchesSelection ? focusValue : undefined,
    savedFocusValue: draftMatchesSelection ? savedFocusValue : undefined,
    displaySaveButton: draftMatchesSelection ? saveVisible : false,
    setFocusDraft,
    commitSavedFocus,
    applyServerFocus,
    initialFocusValue: initialFocus.get(selectionKey),
    selectionKey,
  }
}

export default useHardwareCamera
